// Exam Date Collection Modal
// Shows after language selection, during dashboard loading (non-blocking)
// Collects exam date for personalization and pacing

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation as getTranslationHelper } from '../utils/translationHelpers';
import { userAuth } from '../services/userAuth';
import { trackOnboardingEvent, upsertUserProfile } from '../services/supabaseService';
import './ExamDateModal.css';

const STORAGE_KEY = 'userExamDate';
const STORAGE_KEY_SKIPPED = 'examDateSkipped';

interface ExamDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (date: string | null) => void;
}

export const ExamDateModal: React.FC<ExamDateModalProps> = ({ isOpen, onClose, onSave }) => {
  const { t_nested } = useLanguage();
  const [selectedDate, setSelectedDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Load existing exam date if available
  useEffect(() => {
    if (isOpen) {
      const savedDate = localStorage.getItem(STORAGE_KEY);
      if (savedDate) {
        try {
          const date = new Date(savedDate);
          // Convert to YYYY-MM-DD format for date input
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          setSelectedDate(`${year}-${month}-${day}`);
        } catch (e) {
          // Invalid date, ignore
        }
      }
    } else {
      // Reset when modal closes
      setSelectedDate('');
      setError(null);
    }
  }, [isOpen]);

  const handleSave = () => {
    setError(null);

    // Validate date is selected
    if (!selectedDate) {
      setError(getTranslationHelper(t_nested, 'examDate.invalidDate', 'Please select a date'));
      return;
    }

    const date = new Date(selectedDate);
    
    // Validate date is valid
    if (isNaN(date.getTime())) {
      setError(getTranslationHelper(t_nested, 'examDate.invalidDate', 'Please enter a valid date'));
      return;
    }

    // Validate date is in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare dates only
    if (date < today) {
      setError(getTranslationHelper(t_nested, 'examDate.pastDate', 'Exam date must be in the future'));
      return;
    }

    // Validate date is not too far in the future (reasonable limit: 10 years)
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 10);
    if (date > maxDate) {
      setError(getTranslationHelper(t_nested, 'examDate.tooFarFuture', 'Exam date must be within the next 10 years'));
      return;
    }

    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, date.toISOString());
    localStorage.removeItem(STORAGE_KEY_SKIPPED);

    const dateStr = date.toISOString().split('T')[0];
    const user = userAuth.getCurrentUser();
    if (user && !user.isGuest) {
      trackOnboardingEvent(user.id, 'exam_date_set', { exam_date: dateStr }).catch(() => {});
      upsertUserProfile(user.id, { exam_date: dateStr }).catch(() => {});
    }

    onSave(date.toISOString());
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY_SKIPPED, 'true');
    onSave(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="exam-date-modal-overlay" onClick={handleSkip}>
      <div className="exam-date-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="exam-date-modal-title">
          {getTranslationHelper(t_nested, 'examDate.title', 'When is your exam?')}
        </h2>

        <div className="exam-date-input-group">
          <input
            type="date"
            className="exam-date-picker"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setError(null);
            }}
            min={new Date().toISOString().split('T')[0]} // Prevent past dates
            max={new Date(new Date().setFullYear(new Date().getFullYear() + 10)).toISOString().split('T')[0]} // Max 10 years
          />
          {error && (
            <div className="exam-date-error">{error}</div>
          )}
        </div>

        <div className="exam-date-modal-actions">
          <button
            type="button"
            className="exam-date-btn-primary"
            onClick={handleSave}
          >
            {getTranslationHelper(t_nested, 'examDate.save', 'Save date')}
          </button>
          <button
            type="button"
            className="exam-date-btn-secondary"
            onClick={handleSkip}
          >
            {getTranslationHelper(t_nested, 'examDate.skip', 'I don\'t know yet')}
          </button>
        </div>
      </div>
    </div>
  );
};
